import { useEffect, useState } from "react";
import { ExternalLink, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";

export const DEALERTRACK_CREDIT_APP_URL = "https://secure.accelerate.dealer.com/dealer/25789";
const IFRAME_WIDTH = 920;
const IFRAME_HEIGHT = 1200;
const MOBILE_QUERY = "(max-width: 767px)";

const GUIDE_SECTIONS = [
  {
    titleKey: "financing.section1Title",
    rows: [
      { key: "financing.firstName", en: "First Name" },
      { key: "financing.middleName", en: "Middle Name" },
      { key: "financing.lastName", en: "Last Name" },
      { key: "financing.email", en: "Email" },
      { key: "financing.mobileNumber", en: "Mobile Phone" },
      { key: "financing.homeNumber", en: "Home Phone" },
      { key: "financing.dateOfBirth", en: "Date of Birth" },
      { key: "financing.ssn", en: "Social Security Number" },
      { key: "financing.itin", en: "ITIN" },
      { key: "financing.driverLicense", en: "Driver License Number" },
      { key: "financing.dlState", en: "Driver License State" },
    ],
  },
  {
    titleKey: "financing.residenceTitle",
    rows: [
      { key: "financing.addressLine1", en: "Street Address" },
      { key: "financing.city", en: "City" },
      { key: "financing.state", en: "State" },
      { key: "financing.zipCode", en: "ZIP Code" },
      { key: "financing.residenceType", en: "Housing Status / Residence Type" },
      { key: "financing.residenceYears", en: "Time at Address" },
      { key: "financing.rentMortgage", en: "Monthly Rent / Mortgage" },
    ],
  },
  {
    titleKey: "financing.section2Title",
    rows: [
      { key: "financing.employer", en: "Employer" },
      { key: "financing.occupation", en: "Occupation / Job Title" },
      { key: "financing.monthlyIncome", en: "Gross Monthly Income" },
      { key: "financing.jobYears", en: "Time at Job" },
      { key: "financing.workPhone", en: "Work Phone" },
    ],
  },
  {
    titleKey: "financing.section3Title",
    rows: [
      { key: "financing.vehicleYear", en: "Year" },
      { key: "financing.vehicleMake", en: "Make" },
      { key: "financing.vehicleModel", en: "Model" },
      { key: "financing.vin", en: "VIN" },
      { key: "financing.mileage", en: "Mileage" },
    ],
  },
  {
    titleKey: "financing.coBuyerTitle",
    rows: [
      { key: "financing.coApplicant", en: "Co-Applicant / Co-Buyer" },
      { key: "financing.disclosures", en: "Acknowledgments / Consent" },
    ],
  },
];

function SpanishCreditGuide({ t, isDark, accentBg }) {
  const card = isDark
    ? "bg-[#0A0A0A] border border-white/10"
    : "bg-white border border-gray-200 shadow-sm";
  const title = isDark ? "text-white" : "text-gray-900";
  const muted = isDark ? "text-gray-400" : "text-gray-600";
  const heading = isDark ? "text-blue-400" : "text-red-700";
  const rowBorder = isDark ? "border-white/10" : "border-gray-100";
  const enCls = isDark ? "text-white/80" : "text-gray-700";
  const warnBox = isDark
    ? "bg-amber-500/10 border border-amber-500/30 text-amber-200"
    : "bg-amber-50 border border-amber-200 text-amber-900";

  return (
    <div className={`${card} p-5 md:p-6 mb-6 w-full`} data-testid="dealertrack-spanish-guide">
      <div className="flex items-start gap-3 mb-3">
        <Languages className={`w-5 h-5 mt-0.5 shrink-0 ${heading}`} />
        <div>
          <h2 className={`font-['Unbounded'] text-lg font-semibold ${title}`}>
            {t("financing.iframeGuideTitle")}
          </h2>
          <p className={`font-['Manrope'] text-sm mt-2 ${muted}`}>
            {t("financing.iframeGuideIntro")}
          </p>
        </div>
      </div>

      <a
        href={DEALERTRACK_CREDIT_APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="block mb-3"
      >
        <Button
          className={`w-full sm:w-auto rounded-none py-5 px-6 text-white uppercase tracking-wider ${accentBg}`}
          data-testid="dealertrack-translate-form"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          {t("financing.iframeTranslateBtn")}
        </Button>
      </a>
      <p className={`font-['Manrope'] text-sm mb-2 ${title}`}>{t("financing.iframeTranslateAfter")}</p>
      <ol className={`font-['Manrope'] text-sm space-y-2 mb-3 list-decimal pl-5 ${muted}`}>
        <li>{t("financing.iframeTranslateStepDesktop")}</li>
        <li>{t("financing.iframeTranslateStepIphone")}</li>
        <li>{t("financing.iframeTranslateStepAndroid")}</li>
      </ol>
      <p
        className={`font-['Manrope'] text-xs leading-relaxed p-3 mb-5 ${warnBox}`}
        data-testid="dealertrack-translate-warning"
      >
        {t("financing.iframeTranslateWarning")}
      </p>
      <p className={`font-['Manrope'] text-sm mb-4 ${muted}`}>{t("financing.iframeGuideUse")}</p>

      <div className="hidden sm:grid grid-cols-2 gap-4 mb-2 px-1">
        <span className={`text-[11px] uppercase tracking-wider font-['Manrope'] ${muted}`}>
          {t("financing.iframeInSpanish")}
        </span>
        <span className={`text-[11px] uppercase tracking-wider font-['Manrope'] ${muted}`}>
          {t("financing.iframeOnForm")}
        </span>
      </div>

      <div className="space-y-5">
        {GUIDE_SECTIONS.map((section) => (
          <div key={section.titleKey}>
            <h3 className={`font-['Manrope'] text-xs uppercase tracking-wider font-semibold mb-2 ${heading}`}>
              {t(section.titleKey)}
            </h3>
            <dl>
              {section.rows.map((row) => (
                <div
                  key={row.key}
                  className={`grid grid-cols-1 sm:grid-cols-2 gap-0.5 sm:gap-4 py-1.5 border-b ${rowBorder}`}
                >
                  <dt className={`font-['Manrope'] text-sm ${title}`}>{t(row.key)}</dt>
                  <dd className={`font-['Manrope'] text-sm ${enCls}`}>{row.en}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DealertrackCreditApp() {
  const { t, language } = useTranslation();
  const { isDark } = useTheme();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(MOBILE_QUERY).matches : false
  );

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  const isSpanish = language === "es";
  const accentBg = isDark ? "bg-blue-600 hover:bg-blue-700" : "bg-red-600 hover:bg-red-700";
  const muted = isDark ? "text-gray-400" : "text-gray-600";

  const guide = isSpanish ? (
    <SpanishCreditGuide t={t} isDark={isDark} accentBg={accentBg} />
  ) : null;

  if (isMobile) {
    return (
      <div className="w-full" data-testid="dealertrack-mobile-cta">
        {guide}
        <div className="max-w-xl mx-auto text-center space-y-5">
          <p className={`font-['Manrope'] text-sm ${muted}`}>{t("financing.iframeMobileHint")}</p>
          <a href={DEALERTRACK_CREDIT_APP_URL} target="_blank" rel="noopener noreferrer">
            <Button
              className={`w-full rounded-none py-6 text-white uppercase tracking-wider ${accentBg}`}
              data-testid="dealertrack-open-new-tab"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {t("financing.iframeOpenApp")}
            </Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center" data-testid="dealertrack-iframe-wrap">
      {guide}
      <iframe
        title={t("financing.iframeTitle")}
        src={DEALERTRACK_CREDIT_APP_URL}
        width={IFRAME_WIDTH}
        height={IFRAME_HEIGHT}
        scrolling="yes"
        className="max-w-full bg-white border-0"
        style={{ width: "100%", maxWidth: `${IFRAME_WIDTH}px`, height: `${IFRAME_HEIGHT}px` }}
        data-testid="dealertrack-credit-iframe"
      />
      <a
        href={DEALERTRACK_CREDIT_APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-4 text-sm font-['Manrope'] underline underline-offset-4 ${
          isDark ? "text-blue-400 hover:text-blue-300" : "text-red-600 hover:text-red-700"
        }`}
      >
        {t("financing.iframeFallback")}
      </a>
    </div>
  );
}
